'use client'

/**
 * Sticky bottom conversion bar — keeps the primary CTA one tap away.
 * Revealed only after the hero enroll card scrolls out of view, so the
 * two CTAs don't compete on first paint (design note). Reveal is driven
 * by an IntersectionObserver on the hero element.
 *
 * Sits at z-40 — below the site header (z-50) and the floating actions
 * cluster, above page content.
 */
import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import type { CourseView } from './types'

/**
 * CTA label key (`courseDetail.enroll.*`) + href for the bar, derived from
 * the course's CTA mode. Same copy as the hero enroll card.
 */
function barCta(course: CourseView): { labelKey: string; href: string } {
  const { cta, progress } = course
  switch (cta.mode) {
    case 'continue':
      return { labelKey: 'continueCourse', href: cta.continueHref }
    case 'start':
    case 'enroll':
      // Visiting the first item auto-enrolls, so 'enroll' can be a link.
      return { labelKey: 'beginCourse', href: progress?.continueHref || cta.continueHref }
    case 'start_free':
      // Signed-out visitor on a free course — plain link straight into the
      // first lesson, no sign-in (progress isn't tracked for anon).
      return { labelKey: 'startCourse', href: cta.continueHref }
    case 'signin':
      return { labelKey: 'signInToEnroll', href: cta.signInHref }
    case 'subscribe':
      return { labelKey: 'subscribeToAccess', href: cta.subscribeHref }
    default: // register
      return { labelKey: 'becomeMember', href: cta.registerHref }
  }
}

export function StickyCtaBar({
  course,
  observeId = 'course-hero',
}: {
  course: CourseView
  observeId?: string
}) {
  const [shown, setShown] = useState(false)
  const t = useTranslations('courseDetail')

  useEffect(() => {
    const hero = document.getElementById(observeId)
    if (!hero) return // hero always present on this page; stay hidden otherwise
    // Reveal the bar once the hero (which holds the enroll card) has
    // scrolled fully out of view, so the two CTAs don't compete on load.
    // The observer reports the initial intersection state on observe(),
    // asynchronously — no synchronous setState needed here.
    const io = new IntersectionObserver(
      ([entry]) => setShown(!entry.isIntersecting),
      { threshold: 0 },
    )
    io.observe(hero)
    return () => io.disconnect()
  }, [observeId])

  // No curriculum yet → no CTA to surface (the enroll card shows a
  // coming-soon state instead). Hooks above run unconditionally.
  if (course.lessonCount === 0) return null

  const { labelKey, href } = barCta(course)
  const label = t(`enroll.${labelKey}`)
  const subline = course.progress
    ? `${t('sticky.percentComplete', { percent: course.progress.percent })}${
        course.progress.upNextTitle
          ? ` · ${t('sticky.upNext', { title: course.progress.upNextTitle })}`
          : ''
      }`
    : `${course.isGated ? `${t('enroll.membersOnly')} · ` : ''}${t('lessonsCount', {
        count: course.sectionCount,
      })}`

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-[rgba(255,255,255,0.96)] backdrop-blur-[8px] transition-transform duration-[220ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
        shown ? 'translate-y-0' : 'translate-y-full'
      }`}
      aria-hidden={!shown}
    >
      <div className="mx-auto flex max-w-[1180px] items-center gap-3 px-10 py-3.5">
        {course.coverUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={optimizeSanityImageUrl(course.coverUrl, 96)}
            alt=""
            className="h-[46px] w-[46px] shrink-0 object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-ink">{course.title}</p>
          <p className="truncate text-xs text-muted-foreground">{subline}</p>
        </div>
        <Link
          href={href}
          className="shrink-0 rounded-[12px] bg-primary px-7 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 active:opacity-70"
        >
          {label}
        </Link>
      </div>
    </div>
  )
}
